import { useState, useCallback } from 'react';
import { Head } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { PersonalInfoSection } from './PersonalInfoSection';
import { BillingInfoSection } from './BillingInfoSection';
import { NotificationsSection } from './NotificationsSection';
import { useProfileApi } from '../../hooks/useProfileApi';
import type { ProfileUser, ApiResponseWithUser } from '../../types/profile';

interface ProfileProps {
    user: ProfileUser;
    routes: {
        update: string;
    };
}

export default function Profile({ user: initialUser, routes }: ProfileProps) {
    const [user, setUser] = useState<ProfileUser>(initialUser);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const api = useProfileApi(routes.update);

    const handleUpdate = useCallback(async (data: Partial<ProfileUser>): Promise<ApiResponseWithUser> => {
        setSuccessMessage(null);
        const fullData = { ...user, ...data };
        const result = await api.updateProfile(fullData);

        if (result.success && result.user) {
            setUser(result.user);
            setSuccessMessage(result.message);
            setTimeout(() => setSuccessMessage(null), 4000);
        }

        return result;
    }, [api, user]);

    return (
        <MainLayout>
            <Head>
                <title>Mi perfil</title>
                <meta name="robots" content="noindex" />
            </Head>

            {/* Layout option 2: wide container, cards in grid */}
            <div className="mx-auto max-w-6xl px-4 py-6">
                <PageHeader
                    title="Mi perfil"
                    subtitle="Administrá tu información personal, de facturación y notificaciones."
                />

                {successMessage && (
                    <div className="mb-6 flex items-center gap-2.5 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {successMessage}
                    </div>
                )}

                <div className="grid gap-8 md:grid-cols-2">
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <PersonalInfoSection user={user} onUpdate={handleUpdate} isLoading={api.isLoading} />
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <BillingInfoSection user={user} onUpdate={handleUpdate} isLoading={api.isLoading} />
                    </div>
                </div>
                <div className="mt-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                    <NotificationsSection />
                </div>
            </div>
        </MainLayout>
    );
}
